import React from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import AddressDropdown from '../../address/AddressDropdown';
import LoadingSpinner from '../../common/LoadingSpinner';

const RefundCalculationDisplay = ({ 
  pendingReturnItems, 
  usageFee, 
  estimatedReturnShipping, 
  currency,
  calculatedRefund,
  isLoadingShipping = false,
  // Address dropdown props
  selectedReturnAddress,
  merchantAddresses,
  isLoadingAddresses,
  onAddressChange,
  onAddressSuccess
}) => {
  // Calculate total item value using orderItem price and currency
  const totalItemValue = pendingReturnItems.reduce((sum, item) => {
    // Use item.price (from order-item) instead of item.amount
    const itemPrice = parseFloat(item.price) || 0;
    const itemQuantity = item.quantity || 1;
    return sum + (itemPrice * itemQuantity);
  }, 0);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      {/* Return Address Selection - moved from DecisionControls */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 mb-4">
        <label className="block text-md font-medium text-gray-900 mb-3">Return Address</label>
        <p className="text-sm text-gray-600 mb-3">Select the address where the customer should ship the returned items</p>
        
        {isLoadingAddresses ? (
          <div className="flex justify-center py-2">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <AddressDropdown
            value={selectedReturnAddress?.id || ''}
            onChange={onAddressChange}
            name="returnAddress"
            label=""
            fluid={true}
            placeholder="Select return address"
            showAddNew={true}
            required={true}
            onSuccess={onAddressSuccess}
          />
        )}
      </div>

      <div className="flex items-center mb-3">
        {/* <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2" /> */}
        <h4 className="font-medium text-gray-900">Refund Calculation</h4>
      </div>
      
      <div className="space-y-2 text-sm">
        {/* Item Value */}
        <div className="flex justify-between">
          <span className="text-gray-600">
            Item Value ({pendingReturnItems.length} item{pendingReturnItems.length !== 1 ? 's' : ''})
          </span>
          <span className="font-medium text-gray-900">
            +{formatCurrency(totalItemValue, currency)}
          </span>
        </div>
        
        {/* Return Shipping Cost */}
        <div className="flex justify-between">
          <span className="text-gray-600">
            Est. Return Shipping
            {isLoadingShipping && <span className="ml-1 text-xs">(calculating...)</span>}
          </span>
          <span className="font-medium text-red-600">
            -{formatCurrency(estimatedReturnShipping, currency)}
          </span>
        </div>
        
        {/* Usage Fee */}
        {usageFee && parseFloat(usageFee) > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Usage Fee</span>
            <span className="font-medium text-red-600">
              -{formatCurrency(parseFloat(usageFee), currency)}
            </span>
          </div>
        )}
        
        {/* Divider */}
        <hr className="border-gray-300" />
        
        {/* Final Refund */}
        <div className="flex justify-between font-medium text-base">
          <span className="text-gray-900">Customer Refund</span>
          <span className={calculatedRefund >= 0 ? 'text-green-600' : 'text-red-600'}>
            {formatCurrency(calculatedRefund, currency)}
          </span>
        </div>
      </div>
      
      {/* Warning for negative refunds only */}
      {calculatedRefund < 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-red-800 font-medium">Negative Refund</p>
              <p className="text-red-700 mt-1">
                Customer would owe money. Please reduce the usage fee or reject the return.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundCalculationDisplay; 